import React, { createContext, useContext, useState, useEffect } from 'react';
import { PropertyFavorite } from '../types/realEstate';
import { useAuth } from './AuthContext';
import { toast } from 'react-hot-toast';

interface PropertyFavoritesContextType {
  favorites: PropertyFavorite[];
  addToFavorites: (projectId: string) => Promise<void>;
  removeFromFavorites: (projectId: string) => Promise<void>;
  isInFavorites: (projectId: string) => boolean;
  loading: boolean;
}

const PropertyFavoritesContext = createContext<PropertyFavoritesContextType | undefined>(undefined);

export function PropertyFavoritesProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState<PropertyFavorite[]>([]);
  const [loading, setLoading] = useState(true);

  // Load favorites from localStorage when user logs in
  useEffect(() => {
    const loadFavorites = async () => {
      if (!user) {
        setFavorites([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const savedFavorites = localStorage.getItem(`property_favorites_${user.uid}`);
        setFavorites(savedFavorites ? JSON.parse(savedFavorites) : []);
      } catch (error) {
        console.error('Failed to load property favorites:', error);
        toast.error('Failed to load your favorite properties');
      } finally {
        setLoading(false);
      }
    };

    loadFavorites();
  }, [user]);

  // Save favorites to localStorage
  useEffect(() => {
    if (user) {
      localStorage.setItem(`property_favorites_${user.uid}`, JSON.stringify(favorites));
    }
  }, [favorites, user]);

  const addToFavorites = async (projectId: string) => {
    if (!user) {
      toast.error('Please log in to add properties to favorites');
      return;
    }

    try {
      const newFavorite: PropertyFavorite = {
        id: Date.now().toString(),
        userId: user.uid,
        projectId,
        createdAt: new Date().toISOString()
      };

      setFavorites(prev => {
        const exists = prev.some(fav => fav.projectId === projectId);
        if (exists) return prev;
        return [...prev, newFavorite];
      });

      toast.success('Added to favorite properties');
    } catch (error) {
      console.error('Add to favorites error:', error);
      toast.error('Failed to add to favorites');
    }
  };

  const removeFromFavorites = async (projectId: string) => {
    if (!user) return;

    try {
      setFavorites(prev => prev.filter(fav => fav.projectId !== projectId));
      toast.success('Removed from favorite properties');
    } catch (error) {
      console.error('Remove from favorites error:', error);
      toast.error('Failed to remove from favorites');
    }
  };

  const isInFavorites = (projectId: string) => {
    if (!user) return false;
    return favorites.some(fav => fav.projectId === projectId);
  };

  return (
    <PropertyFavoritesContext.Provider
      value={{
        favorites,
        addToFavorites,
        removeFromFavorites,
        isInFavorites,
        loading
      }}
    >
      {children}
    </PropertyFavoritesContext.Provider>
  );
}

export function usePropertyFavorites() {
  const context = useContext(PropertyFavoritesContext);
  if (context === undefined) {
    throw new Error('usePropertyFavorites must be used within a PropertyFavoritesProvider');
  }
  return context;
}